import cx from "classnames";
import { ChangeEvent, KeyboardEvent, RefObject } from "react";

const MAX_FRACTION_DIGITS = 8;

function formatWithCommas(rawValue: string | number): string {
  const value = String(rawValue ?? "");
  if (!value) return "";

  const [intPart, fracPart] = value.split(".");
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if (fracPart === undefined) {
    return formattedInt;
  }

  const trimmedFrac = fracPart.slice(0, MAX_FRACTION_DIGITS);

  return fracPart !== undefined ? `${formattedInt}.${trimmedFrac}` : formattedInt;
}

type Props = {
  value?: string | number;
  inputRef?: RefObject<HTMLInputElement>;
  onValueChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  placeholder?: string;
  qa?: string;
  isDisabled?: boolean;
};

function NumberInput({
  value = "",
  inputRef,
  onValueChange,
  onFocus,
  onBlur,
  onKeyDown,
  className,
  placeholder,
  qa,
  isDisabled = false,
}: Props) {
  const displayValue = formatWithCommas(value);

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    if (!onValueChange) return;

    // Strip grouping commas, keep dot as decimal separator
    let normalized = e.target.value.replace(/,/g, "");

    // Enforce at most MAX_FRACTION_DIGITS digits after the decimal point
    const [, frac = ""] = normalized.split(".");
    if (frac.length > MAX_FRACTION_DIGITS) {
      return;
    }

    if (normalized === ".") {
      normalized = "0.";
    }

    if (normalized === "" || /^\d*\.?\d*$/.test(normalized)) {
      e.target.value = normalized;
      onValueChange(e);
    }
  }

  return (
    <input
      data-qa={qa}
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      className={cx(className, "text-white placeholder:text-gray-lighter")}
      value={displayValue}
      ref={inputRef}
      onChange={onChange}
      autoComplete="off"
      autoCorrect="off"
      minLength={1}
      maxLength={30}
      spellCheck="false"
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      disabled={isDisabled}
    />
  );
}

export default NumberInput;
